import React from "react";
import {
  HStack,
  IconButton,
  Table,
  TableContainer,
  Tbody,
  Td,
  Th,
  Thead,
  Tr,
  useColorMode,
} from "@chakra-ui/react";
import NoteIndicator from "../study/NoteIndicator";

// Column id, header label (rendered as `<span>` with optional <br />), and the
// row property used as the sort key. Cell rendering is uniform: read `trade[key]`,
// honor isNumeric for the strike/avg-price/units/pnl/percent_wl columns.
const COLUMNS = [
  { key: "trade_type", header: ["Trade", "Type"], aria: "Sort by Trade Type" },
  { key: "security_type", header: ["Security", "Type"], aria: "Sort by Security Type" },
  { key: "trade_direction", header: ["Direction"], aria: "Sort by Direction" },
  { key: "ticker_name", header: ["Ticker"], aria: "Sort by Ticker" },
  { key: "trade_date", header: ["Close", "Date"], aria: "Sort by Close Date" },
  { key: "expiry", header: ["Expiry"], aria: "Sort by Expiry Date" },
  { key: "strike", header: ["Strike"], aria: "Sort by Strike Price", isNumeric: true },
  { key: "buy_value", header: ["Avg", "Price"], aria: "Sort by Avg Price", isNumeric: true },
  { key: "units", header: ["# of", "Units"], aria: "Sort by # of Units", isNumeric: true },
  { key: "rr", header: ["R/R"], aria: "Sort by r/r" },
  { key: "pnl", header: ["PNL"], aria: "Sort by pnl", isNumeric: true },
  { key: "fees", header: ["Fees"], aria: "Sort by Fees", isNumeric: true },
  { key: "percent_wl", header: ["% W/L"], aria: "Sort by % W/L", isNumeric: true },
];

const renderHeaderLabel = (lines) =>
  lines.flatMap((line, idx) =>
    idx === 0 ? [line] : [<br key={idx} />, line]
  );

const TradeTable = ({
  hasTrades,
  sortedTrades = [],
  selectedRow,
  onRowClick,
  requestSort,
  getSortIcon,
}) => {
  const { colorMode } = useColorMode();
  const headerBg = colorMode === "light" ? "white" : "gray.700";
  const borderColor = colorMode === "light" ? "gray.100" : "gray.800";

  if (!hasTrades) {
    return (
      <TableContainer overflowY="auto" overflowX="auto" rounded="lg">
        <Table
          size="sm"
          variant="simple"
          colorScheme="gray"
          borderWidth="1px"
          borderColor={borderColor}
        >
          <Thead position="sticky" top={0} bgColor={headerBg} zIndex={2}>
            <Tr>
              {COLUMNS.map((col) => (
                <Th key={col.key} resize="horizontal" overflow="auto">
                  {col.header.join(" ")}
                </Th>
              ))}
              <Th resize="horizontal" overflow="auto">Comments</Th>
            </Tr>
          </Thead>
        </Table>
      </TableContainer>
    );
  }

  return (
    <TableContainer overflowY="auto" overflowX="auto" rounded="lg">
      <Table
        size="sm"
        variant="simple"
        colorScheme="gray"
        borderColor={borderColor}
      >
        <Thead position="sticky" top={0} bgColor={headerBg} zIndex={2}>
          <Tr>
            {COLUMNS.map((col) => (
              <Th key={col.key} resize="horizontal" overflow="auto">
                <HStack>
                  <span>{renderHeaderLabel(col.header)}</span>
                  <IconButton
                    aria-label={col.aria}
                    icon={getSortIcon(col.key)}
                    onClick={() => requestSort(col.key)}
                    size="xs"
                    variant="ghost"
                  />
                </HStack>
              </Th>
            ))}
            <Th resize="horizontal" overflow="hidden" textOverflow="ellipsis">
              Comments
            </Th>
          </Tr>
        </Thead>
        <Tbody zIndex={1}>
          {sortedTrades.map((trade, index) => {
            const isSelected = selectedRow.includes(index);
            const rowBg =
              colorMode === "light"
                ? isSelected
                  ? "gray.200"
                  : "white"
                : isSelected
                ? "gray.900"
                : "gray.700";
            return (
              <Tr
                key={trade.trade_id ?? index}
                onClick={() => onRowClick(trade, index)}
                bgColor={rowBg}
                _hover={{ bg: colorMode === "light" ? "gray.50" : "gray.600" }}
                cursor="pointer"
              >
                {COLUMNS.map((col) => (
                  <Td key={col.key} isNumeric={col.isNumeric}>
                    {col.key === "ticker_name" ? (
                      <HStack spacing={1}>
                        <span>{trade[col.key]}</span>
                        <NoteIndicator ticker={trade[col.key]} />
                      </HStack>
                    ) : (
                      trade[col.key]
                    )}
                  </Td>
                ))}
                <Td whiteSpace="normal" overflow="hidden">
                  {trade.comments}
                </Td>
              </Tr>
            );
          })}
        </Tbody>
      </Table>
    </TableContainer>
  );
};

export default TradeTable;
